import { useEffect, useMemo, useState } from "react";
import { MacroStrip, ItemCard, NotesPoints, splitPoints } from "@/components/MealViewParts";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, Flame, Pizza, Zap, Plus, X, Loader2,
  Pencil, Camera, MessageSquareText, Bookmark, Calculator, Trash2, Footprints,
  Barcode as BarcodeIcon, BookOpen, Sparkles, Edit3, ScanLine,
  Bike, Waves, Dumbbell, HeartPulse, Activity,
} from "lucide-react";
import api from "@/lib/api";
import HealthKitStepsSync from "@/components/HealthKitStepsSync";
import HealthKitConnectInline from "@/components/HealthKitConnectInline";
import { HealthConnectSync, HealthConnectInline } from "@/components/HealthConnect";
import { useSWR, invalidateSWRPrefix } from "@/lib/swr";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import DayNotesCard from "@/components/DayNotesCard";
import Logo from "@/components/Logo";
import StreakModal from "@/components/StreakModal";
import PushPromptCard from "@/components/PushPromptCard";
import WhatsNewBarcode, { shouldShowBarcodeNews } from "@/components/WhatsNewBarcode";
import SupportCheckIn, { shouldShowSupportCheckIn } from "@/components/SupportCheckIn";
import { mealTypeByTime } from "@/components/AddRecipeToMealModal";
import { shouldShowPushPrompt } from "@/lib/pushPromptGate";
import FeatureTipModal from "@/components/FeatureTipModal";
import { shouldShowTip } from "@/lib/featureTips";
import { isCapacitorIOS, initIOSPush } from "@/lib/nativePush";
import { todayISO, fmtInt } from "@/lib/utils";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

/* =====================================================================
 * Home / Dashboard — iOS mockup parity
 * Header + month/day strip + calorie hero + macros + meals + water + steps + activity
 * ===================================================================== */

// Locale-aware month & day names — driven by Intl so we don't hand-maintain
// two hard-coded dictionaries. When lang="en" we get "January", "Sun"; when
// lang="ar" we get "يناير", "أحد".
const MONTHS = (lang) => Array.from({ length: 12 }, (_, i) =>
  new Date(2020, i, 1).toLocaleDateString(lang === "en" ? "en-US" : "ar-EG", { month: "long" })
);
const DAYS_SHORT = (lang) => Array.from({ length: 7 }, (_, i) =>
  new Date(2020, 5, 7 + i).toLocaleDateString(lang === "en" ? "en-US" : "ar-EG", { weekday: "short" })
);

// Meal-type keys — labels resolve via i18n at render.
const MEAL_TYPE_KEYS = [
  { key: "breakfast", tKey: "meal.breakfast", emoji: "🍳" },
  { key: "lunch",     tKey: "meal.lunch",     emoji: "🥗" },
  { key: "dinner",    tKey: "meal.dinner",    emoji: "🍽️" },
  { key: "snack",     tKey: "meal.snack",     emoji: "🍎" },
];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const en = lang === "en";
  const AR_MONTHS = useMemo(() => MONTHS(lang), [lang]);
  const AR_DAYS_SHORT = useMemo(() => DAYS_SHORT(lang), [lang]);
  const MEAL_TYPES = useMemo(
    () => MEAL_TYPE_KEYS.map((m) => ({ ...m, label: t(m.tKey) })),
    [t, lang],
  );
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Landing directly on /app?date=YYYY-MM-DD (e.g. after saving a meal on a
  // past day) reopens that same day instead of snapping back to today.
  const initialDate = (() => {
    const d = searchParams.get("date");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    return todayISO();
  })();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [hkKey, setHkKey] = useState(0);
  const [modal, setModal] = useState(null); // { kind: 'steps_log' | 'steps_goal' | 'water_add' | 'water_edit' | 'exercise' | 'log_meal', entry?: {} }
  const [logMealType, setLogMealType] = useState(null);
  const [expandedMeal, setExpandedMeal] = useState(null); // meal.id
  const [streakOpen, setStreakOpen] = useState(false);
  const [pushPromptOpen, setPushPromptOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const isToday = selectedDate === todayISO();

  // Stale-while-revalidate cache — on second-and-later visits to Home the
  // dashboard paints INSTANTLY from the last-seen response, while a fresh
  // fetch runs silently in the background. The user never sees a spinner
  // when switching tabs / coming back from Add Meal.
  const dashKey = `dash:${selectedDate}`;
  const { data: dashData, refresh: reloadDash } = useSWR(dashKey, async () => {
    const [{ data: s }, { data: w }] = await Promise.all([
      api.get(`/summary/today?date=${selectedDate}`),
      api.get(`/water?date=${selectedDate}`),
    ]);
    return { summary: s, waterEntries: w.entries || [] };
  });
  const summary = dashData?.summary || null;
  const waterEntries = dashData?.waterEntries || [];

  // Ask for push notification permission a couple of seconds after
  // the user lands on Home — but ALWAYS show our friendly benefits
  // card first (never fire Apple's dialog directly). If they tap
  // "Enable" the card fires the appropriate native (APNs) or web
  // (VAPID) permission prompt. Guarded by localStorage so it's a
  // one-shot per install.
  //
  // The 2 s delay lets the Home content paint + settle animations
  // — feels intentional, not intrusive. Industry-standard pattern
  // (Duolingo / TikTok / Instagram) for permission priming which
  // triples opt-in vs firing the system dialog cold.
  useEffect(() => {
    const t = setTimeout(async () => {
      if (await shouldShowPushPrompt()) setPushPromptOpen(true);
      else if (shouldShowBarcodeNews(user)) setNewsOpen(true);
      else if (shouldShowSupportCheckIn(user)) setCheckInOpen(true);
    }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Any mutation (log meal, add water, delete exercise, ...) invalidates every
  // cached date and refetches the current one so numbers stay accurate.
  const load = async () => {
    invalidateSWRPrefix("dash:");
    await reloadDash();
  };

  // Users can log for TODAY or any PAST day (up to 30 days back). Only future is blocked.
  const isFuture = selectedDate > todayISO();
  const canLog = !isFuture;

  // Compute macros remaining for the ring cards (short labels for mobile)
  const macros = summary?.macros || {};
  const macroRow = [
    { label: t("macro.protein"),  target: macros.protein?.target || 0, consumed: macros.protein?.consumed || 0, color: "#38BDF8", accent: "#DCF3FE", emoji: "🥩" },
    { label: t("macro.fat"),      target: macros.fat?.target     || 0, consumed: macros.fat?.consumed     || 0, color: "#FF8833", accent: "#FFEDD8", emoji: "🥑" },
    { label: t("macro.carbs"),    target: macros.carbs?.target   || 0, consumed: macros.carbs?.consumed   || 0, color: "#22C55E", accent: "#D1FAE5", emoji: "🌾" },
  ];

  const cal_target = summary?.target_calories || 0;
  const cal_consumed = summary?.consumed_calories || 0;
  const cal_burned = summary?.burned_calories || 0;
  const cal_remaining = summary?.remaining_calories ?? (cal_target - cal_consumed + (user?.include_exercise_calories ? cal_burned : 0));

  // Group meals by type
  const mealsByType = useMemo(() => {
    const map = { breakfast: [], lunch: [], dinner: [], snack: [] };
    (summary?.meals || []).forEach((m) => {
      const bucket = map[m.meal_type] || map.snack;
      bucket.push(m);
    });
    return map;
  }, [summary]);

  const totalByType = (type) => (mealsByType[type] || []).reduce((s, m) => s + (m.total_calories || 0), 0);

  const addWaterGlass = async (ml = 250) => {
    if (!canLog) return toast.error(t("dash.future_no_log"));
    await api.post("/water", { date: selectedDate, amount_ml: ml });
    load();
  };

  const removeLastWater = async () => {
    if (!canLog) return;
    const { data } = await api.get(`/water?date=${selectedDate}`);
    const last = (data.entries || [])[data.entries.length - 1];
    if (!last) return;
    await api.delete(`/water/${last.id}`);
    load();
  };

  const openLogMeal = (mealType) => {
    if (!canLog) return toast.error(t("dash.future_no_log"));
    setLogMealType(mealType);
    setModal({ kind: "log_meal" });
  };

  const removeMeal = async (m) => {
    const ok = await confirm({ title: t("dash.delete_meal_title"), description: `${m.name} — ${fmtInt(m.total_calories)} ${t("dash.calories_short")}`, confirmLabel: t("common.delete"), danger: true });
    if (!ok) return;
    await api.delete(`/meals/${m.id}`);
    toast.success(t("dash.deleted"));
    load();
  };

  if (!summary) return <div className="p-8 text-center text-neutral-500">{t("dash.loading")}</div>;

  // Compute the visible 7-day strip centered on selectedDate
  const anchor = new Date(selectedDate + "T00:00:00");
  const toLocalISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const daysStrip = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() - 3 + i);
    return d;
  });

  const shiftMonth = (dir) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + dir * 7);
    if (d > new Date()) return; // no future
    setSelectedDate(toLocalISO(d));
  };

  const water_target = summary.water.target_ml || 2000;
  const water_ml = summary.water.consumed_ml || 0;
  const water_L = (water_ml / 1000).toFixed(2);
  const water_target_L = (water_target / 1000).toFixed(2);
  // Always show 6 glasses in one row (matching iOS mockup)
  const total_glasses = 6;
  const glass_ml = Math.max(100, Math.round(water_target / total_glasses));
  const filled_full = Math.floor(water_ml / glass_ml);
  const partial_frac = Math.min(1, (water_ml % glass_ml) / glass_ml);

  const steps = summary.steps || 0;
  const steps_target = user?.steps_target || 10000;
  const steps_remaining = Math.max(0, steps_target - steps);
  const steps_pct = Math.min(100, (steps / steps_target) * 100);

  return (
    <div dir={lang === "en" ? "ltr" : "rtl"} className="pb-6 bg-transparent min-h-screen">
      {/* ============ Header — logo (right in RTL) + streak (left in RTL) ============ */}
      <div className="px-5 pt-3 pb-3 flex items-center justify-between">
        <Logo size={lang === "en" ? 48 : 80} data-testid="dashboard-brand" />
        <button
          onClick={() => setStreakOpen(true)}
          className="bg-white shadow-card rounded-full px-3.5 py-2 flex items-center gap-1.5 active:scale-95 transition-transform"
          data-testid="dashboard-streak"
          aria-label={t("dash.streak_aria")}
          title={t("dash.streak_aria")}
        >
          <span className="font-black text-lg num">{user?.streak_days || 0}</span>
          <span className="text-lg">🔥</span>
        </button>
      </div>

      <StreakModal open={streakOpen} onClose={() => setStreakOpen(false)} user={user} />
      <PushPromptCard open={pushPromptOpen} onClose={() => { setPushPromptOpen(false); if (shouldShowBarcodeNews(user)) setTimeout(() => setNewsOpen(true), 600); }} />
      <WhatsNewBarcode open={newsOpen} onClose={() => setNewsOpen(false)}
        onTry={() => { setNewsOpen(false); nav(`/app/add?type=${mealTypeByTime()}&mode=barcode`); }} />
      <SupportCheckIn open={checkInOpen} onClose={() => setCheckInOpen(false)} user={user} />

      {/* Past-day hint */}
      {!isToday && !isFuture && (
        <div className="mx-5 mb-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-2xl px-3 py-2 flex items-center gap-2" data-testid="past-day-banner">
          <span className="text-base">📅</span>
          <span>{t("dash.viewing_past_day")}</span>
          <button onClick={() => setSelectedDate(todayISO())}
            className="ms-auto text-brand-600 font-semibold underline"
            data-testid="past-day-back-today">
            {t("dash.back_to_today")}
          </button>
        </div>
      )}

      {/* ============ Month + day strip ============ */}
      <div className="px-5">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => shiftMonth(-1)} className="w-9 h-9 rounded-full flex items-center justify-center text-brand-500" data-testid="dash-week-prev">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="text-lg font-bold" data-testid="dash-month-label">
            {AR_MONTHS[anchor.getMonth()]} {anchor.getFullYear()}
          </div>
          <button onClick={() => shiftMonth(1)} disabled={anchor >= new Date(new Date().setHours(0,0,0,0))} className="w-9 h-9 rounded-full flex items-center justify-center text-brand-500 disabled:opacity-30" data-testid="dash-week-next">
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-4" data-testid="dash-day-strip">
          {daysStrip.map((d, i) => {
            const iso = toLocalISO(d);
            const active = iso === selectedDate;
            const future = d > new Date(new Date().setHours(23,59,59,999));
            return (
              <button
                key={i}
                onClick={() => !future && setSelectedDate(iso)}
                disabled={future}
                className="flex flex-col items-center gap-1 py-1 disabled:opacity-30"
                data-testid={`dash-day-${iso}`}
              >
                <span className="text-[11px] text-neutral-500">{AR_DAYS_SHORT[d.getDay()]}</span>
                <span className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold num ${
                  active ? "bg-brand-500 text-white" : "text-neutral-800"
                }`}>{d.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ============ Calorie hero card ============ */}
      <div className="px-5">
        <div className={`rounded-3xl p-5 relative overflow-hidden shadow-card ${cal_remaining < 0 ? "bg-red-100" : "bg-[#FFE9D0]"}`} data-testid="dashboard-calorie-hero">
          {/* Center remaining badge */}
          <div className="text-center mb-2">
            <div className={`text-4xl sm:text-5xl font-black num tracking-tight leading-tight ${cal_remaining < 0 ? "text-red-600" : ""}`} data-testid="hero-remaining">
              {cal_remaining < 0 ? "+" : ""}{fmtInt(Math.abs(cal_remaining))}
            </div>
            <div className={`text-xs mt-1 ${cal_remaining < 0 ? "text-red-700 font-bold" : "text-neutral-600"}`}>
              {cal_remaining < 0 ? t("dash.calories_over") : t("dash.calories_remaining")}
            </div>
          </div>
          {/* Row: consumed | ring | burned */}
          <div className="flex items-center justify-between mt-2 gap-2">
            {/* Consumed (right in RTL) */}
            <div className="flex-1 flex flex-col items-center min-w-0">
              <Pizza className={`w-7 h-7 mb-1 ${cal_remaining < 0 ? "text-red-500" : "text-brand-500"}`} />
              <div className="text-xl sm:text-2xl font-black num" data-testid="hero-consumed">{fmtInt(cal_consumed)}</div>
              <div className="text-[11px] text-neutral-600 mt-0.5 whitespace-nowrap">{t("dash.consumed")}</div>
            </div>
            {/* Center ring */}
            <div className="flex-shrink-0">
              <CalorieRing consumed={cal_consumed} target={cal_target} size={110} exceeded={cal_remaining < 0} />
            </div>
            {/* Burned (left in RTL) */}
            <div className="flex-1 flex flex-col items-center min-w-0">
              <Flame className={`w-7 h-7 mb-1 ${cal_remaining < 0 ? "text-red-500" : "text-brand-500"}`} />
              <div className="text-xl sm:text-2xl font-black num" data-testid="hero-burned">{fmtInt(cal_burned)}</div>
              <div className="text-[11px] text-neutral-600 mt-0.5 whitespace-nowrap">{t("dash.burned")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ Macros row ============ */}
      <div className="px-5 mt-4 grid grid-cols-3 gap-3">
        {macroRow.map((m) => (
          <MacroCircle key={m.label} {...m} />
        ))}
      </div>

      {/* ============ Meals section ============ */}
      <div className="px-5 mt-6">
        <h2 className="text-xl font-black mb-3 text-right" data-testid="meals-title">{t("dash.meals_title")}</h2>
        <div className="space-y-3">
          {MEAL_TYPES.map((mt) => {
            const meals = mealsByType[mt.key] || [];
            const total = totalByType(mt.key);
            return (
              <div key={mt.key} className="bg-white rounded-3xl shadow-card overflow-hidden" data-testid={`meal-group-${mt.key}`}>
                {/* Group header — emoji + label on the LEFT, + button on the RIGHT */}
                <div className="flex items-center gap-3 p-4" dir="ltr">
                  <div className="w-11 h-11 rounded-2xl bg-brand-50 flex items-center justify-center text-lg flex-shrink-0">
                    {mt.emoji}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-bold" dir="rtl">{mt.label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5" dir="rtl">
                      {meals.length > 0 ? `${fmtInt(total)} ${t("dash.calories_unit")}` : t("dash.no_meals_yet")}
                    </div>
                  </div>
                  <button
                    onClick={() => openLogMeal(mt.key)}
                    className="w-11 h-11 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center flex-shrink-0"
                    data-testid={`meal-add-${mt.key}`}
                  >
                    <Plus className="w-5 h-5 text-neutral-600" />
                  </button>
                </div>
                {/* Meal items — same LEFT/RIGHT layout as the header */}
                {meals.map((m) => (
                  <div key={m.id} className="border-t border-neutral-100" data-testid={`meal-item-${m.id}`}>
                    <button
                      onClick={() => setExpandedMeal(expandedMeal === m.id ? null : m.id)}
                      className="w-full flex items-center gap-3 p-4"
                      dir="ltr"
                      data-testid={`meal-expand-${m.id}`}
                    >
                      <div className="w-11 h-11 rounded-2xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                        <Calculator className="w-5 h-5 text-brand-500" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-semibold truncate" dir="rtl">{m.name}</div>
                        <div className="text-xs text-neutral-500 mt-0.5" dir="rtl">
                          {fmtInt(m.total_calories)} {t("dash.calories_unit")}
                        </div>
                      </div>
                      <div className="text-brand-500 flex-shrink-0" aria-label="expand">
                        <ChevronLeft className={`w-5 h-5 transition-transform ${expandedMeal === m.id ? "-rotate-90" : ""}`} />
                      </div>
                    </button>
                    {expandedMeal === m.id && (
                      <MealDetail
                        meal={m}
                        onChanged={load}
                        onDeleteMeal={() => removeMeal(m)}
                        onEditMeal={() => {
                          // Reuse the same AddMeal screen the user already
                          // knows — passes the meal date so past-day edits
                          // save back to the correct date, and `edit=<id>`
                          // so AddMeal preloads the meal and switches its
                          // save button into "update" mode.
                          const dq = m.date ? `&date=${m.date}` : "";
                          nav(`/app/add?edit=${m.id}${dq}`);
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ Water tracker (rich picker) ============ */}
      <div className="px-5 mt-6">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-xl font-black" data-testid="water-title">{t("dash.water_title")}</h2>
          <button
            onClick={() => setModal({ kind: "water_goal" })}
            className="text-sm text-neutral-500 num flex items-center gap-1.5 active:scale-95 transition-transform"
            dir="ltr"
            data-testid="water-label"
            aria-label={t("dash.water_edit_goal_aria")}
            title={t("dash.water_edit_goal_aria")}
          >
            <span className="w-6 h-6 rounded-full orange-gradient flex items-center justify-center shadow-sm">
              <Pencil className="w-3 h-3 text-white" strokeWidth={2.75} />
            </span>
            <span>{water_L} / {water_target_L}</span>
            <span className="text-xs text-neutral-500 font-semibold">{t("dash.water_liter")}</span>
          </button>
        </div>
        <div className="bg-white rounded-3xl shadow-card p-4">
          <div className="grid grid-cols-6 gap-2" data-testid="water-glasses">
            {waterEntries.map((w, i) => (
              <button key={w.id} onClick={() => setModal({ kind: "water_edit", entry: w })}
                className="flex flex-col items-center gap-1"
                data-testid={`water-glass-${w.id}`}>
                <WaterGlass amountMl={w.amount_ml} fullMl={glass_ml} />
                <div className="text-[10px] text-neutral-600 num">{w.amount_ml}ml</div>
              </button>
            ))}
            {/* Always show + at end */}
            <button onClick={() => setModal({ kind: "water_add" })}
              className="flex flex-col items-center gap-1"
              data-testid="water-glass-add">
              <WaterGlass empty />
              <div className="text-[10px] text-neutral-400">{t("dash.water_add")}</div>
            </button>
          </div>
        </div>
      </div>

      {/* ============ Steps (log + goal) ============ */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-black text-right" data-testid="steps-title">{t("dash.steps_title")}</h2>
          {/* Apple Health transparency badge — mounted always so it can
              trigger the silent HealthKit → backend sync on iOS
              (component renders nothing on Web/Android or if the user
              hasn't connected). Required by Apple Review 2.5.1. */}
          <HealthKitStepsSync key={hkKey} date={selectedDate} onSynced={load} />
          <HealthConnectSync key={`hc-${hkKey}`} date={selectedDate} onSynced={load} />
        </div>
        <div className="bg-white rounded-3xl shadow-card p-4" data-testid="dashboard-steps-card">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-[#FFEDD8] flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">👟</span>
            </div>
            <div className="flex-1 text-end">
              <div className="flex items-baseline gap-1 justify-end">
                <span className="text-brand-500 font-black text-3xl num">{fmtInt(steps)}</span>
                <span className="text-neutral-500 text-sm num">/ {fmtInt(steps_target)}</span>
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {steps >= steps_target ? t("dash.steps_target_beat") : `${t("dash.steps_remaining")} ${fmtInt(steps_remaining)} ${t("dash.steps_unit")}`}
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-2 rounded-full bg-neutral-100 overflow-hidden">
            <div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.min(100, steps_pct)}%` }} />
          </div>
          {/* Action buttons */}
          <div className="mt-3 flex gap-2">
            <button onClick={() => canLog && setModal({ kind: "steps_log" })}
              className="flex-1 orange-gradient text-white font-bold text-sm py-2.5 rounded-full flex items-center justify-center gap-1.5"
              data-testid="steps-log-btn">
              <Plus className="w-4 h-4" /> {t("dash.steps_log_btn")}
            </button>
            <button onClick={() => setModal({ kind: "steps_goal" })}
              className="bg-neutral-100 text-neutral-700 font-semibold text-sm py-2.5 px-4 rounded-full flex items-center gap-1.5"
              data-testid="steps-goal-btn">
              <Pencil className="w-3.5 h-3.5" /> {t("dash.steps_goal_btn")}
            </button>
            {steps > 0 && canLog && (
              <button onClick={async () => {
                if (!(await confirm({ title: t("dash.steps_delete_title"), description: t("dash.steps_delete_body"), confirmLabel: t("common.delete"), danger: true }))) return;
                await api.post("/steps", { date: selectedDate, steps: 0 });
                toast.success(t("dash.deleted"));
                load();
              }}
                className="bg-red-50 text-red-500 w-10 h-10 rounded-full flex items-center justify-center"
                data-testid="steps-delete-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <HealthKitConnectInline onConnected={() => setHkKey((k) => k + 1)} />
          <HealthConnectInline onConnected={() => setHkKey((k) => k + 1)} />
        </div>
      </div>

      {/* ============ Activities ============ */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-black" data-testid="activity-title">{t("dash.activity_title")}</h2>
          <button
            onClick={() => canLog && setModal({ kind: "exercise" })}
            className="orange-gradient text-white font-bold text-sm px-6 py-3 rounded-full shadow-brand"
            data-testid="log-activity-btn"
          >
            {t("dash.activity_log_btn")}
          </button>
        </div>
        {(summary.exercise || []).length === 0 ? (
          <div className="bg-white rounded-3xl border-[1.5px] border-black p-4" data-testid="activity-card">
            <div className="text-center py-6">
              <span className="text-5xl">🏃‍♂️</span>
              <div className="text-xs text-neutral-500 mt-2">{t("dash.activity_none")}</div>
            </div>
          </div>
        ) : (
          <div className="w-full space-y-2" data-testid="activity-card">
            {(summary.exercise || []).map((e) => {
                const { Icon, color } = exerciseVisual(e.name);
                // Translate stored preset names to English on display so the
                // list matches the active UI language. Falls back to the
                // raw stored name for free-form AI-suggested activities
                // (e.g. "climbed a mountain") which have no preset.
                const preset = EXERCISES.find(x => x.name === e.name);
                const displayName = en ? (preset?.name_en || e.name) : e.name;
                return (
                  <div key={e.id}
                       className="group flex items-center gap-3 bg-white rounded-2xl border-[1.5px] border-black p-3 hover:bg-neutral-50 transition-colors"
                       dir={en ? "ltr" : "rtl"}
                       data-testid={`exercise-entry-${e.id}`}>
                    {/* Icon badge */}
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                         style={{ backgroundColor: `${color}18`, border: `1.5px solid ${color}55` }}>
                      <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.2} />
                    </div>
                    {/* Name + duration */}
                    <div className={`flex-1 min-w-0 ${en ? "text-start" : "text-end"}`}>
                      <div className={`flex items-center gap-1.5 ${en ? "justify-start" : "justify-end"}`}>
                        <div className="font-bold text-sm text-neutral-800 truncate">{displayName}</div>
                        {e.counts_toward_target === false ? (
                          <span
                            className="text-[9px] font-bold bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded-full"
                            title={t("dash.activity_not_counted_tt")}
                            data-testid={`exercise-not-credited-${e.id}`}
                          >
                            {t("dash.activity_recorded_only")}
                          </span>
                        ) : (
                          <span
                            className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full"
                            title={t("dash.activity_counted_tt")}
                            data-testid={`exercise-credited-${e.id}`}
                          >
                            {t("dash.activity_counted")}
                          </span>
                        )}
                      </div>
                      <div className={`text-[11px] text-neutral-500 num flex items-center gap-1.5 ${en ? "justify-start" : "justify-end"}`}>
                        <span dir="ltr" className="num">{e.duration_min}</span>
                        <span>{t("dash.activity_minute")}</span>
                      </div>
                    </div>
                    {/* Calories badge */}
                    <div className={`shrink-0 ${en ? "text-start" : "text-end"}`}>
                      <div className="num font-black text-base" style={{ color }} dir="ltr">
                        −{fmtInt(e.calories_burned)}
                      </div>
                      <div className="text-[9px] text-neutral-400">{t("dash.activity_kcal_unit")}</div>
                    </div>
                    {/* Delete */}
                    <button
                      onClick={async () => {
                        if (!window.confirm(en ? `Delete "${displayName}"?` : `حذف "${displayName}"؟`)) return;
                        try {
                          await api.delete(`/exercise/${e.id}`);
                          load();
                        } catch (err) {
                          alert(en ? "Couldn't delete" : "تعذّر الحذف");
                        }
                      }}
                      className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 border-[1.5px] border-red-200 flex items-center justify-center shrink-0 transition-all active:scale-95"
                      data-testid={`exercise-delete-${e.id}`}
                      aria-label={t("dash.activity_delete_aria")}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" strokeWidth={2.4} />
                    </button>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ============ Day Notes (journal + tasks + meal ingredients) ============
          Sits AFTER the exercises card per the user's ask — keeps the top of
          the dashboard focused on numeric tracking and pushes personal
          journaling / meal-planning below the fold so it doesn't crowd the
          calorie hero. Fully date-aware — swipes with the day picker. */}
      <DayNotesCard
        date={selectedDate}
        dayLabel={new Intl.DateTimeFormat("ar-EG", { weekday: "long", day: "numeric", month: "long" }).format(new Date(selectedDate))}
      />

      {/* ============ Modals ============ */}
      {modal?.kind === "steps_log" && (
        <QuickInputModal
          title={t("dash.steps_log_title")} suffix={t("dash.steps_unit")} placeholder="8000" testid="steps-modal"
          initial={steps > 0 ? String(steps) : ""}
          onSubmit={async (n) => { await api.post("/steps", { date: selectedDate, steps: Number(n) }); toast.success(t("dash.steps_logged")); setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === "steps_goal" && (
        <QuickInputModal
          title={t("dash.steps_goal_title")} suffix={t("dash.steps_unit")} placeholder="10000" testid="steps-goal-modal"
          initial={String(steps_target)}
          onSubmit={async (n) => {
            await api.patch("/user/preferences", { steps_target: Number(n) });
            toast.success(t("dash.steps_goal_updated"));
            await refresh();
            setModal(null);
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === "water_add" && (
        <WaterPicker
          onClose={() => setModal(null)}
          onSubmit={async (ml) => {
            await api.post("/water", { date: selectedDate, amount_ml: Number(ml) });
            toast.success(`${t("dash.water_added")} ${ml}ml`);
            setModal(null); load();
          }}
        />
      )}
      {modal?.kind === "water_goal" && (
        <WaterGoalModal
          initial={water_target}
          onClose={() => setModal(null)}
          onSubmit={async (ml) => {
            await api.patch("/user/preferences", { water_target_ml: Number(ml) });
            toast.success(t("dash.water_goal_updated"));
            await refresh();
            setModal(null);
            load();
          }}
        />
      )}
      {modal?.kind === "water_edit" && (
        <WaterEditSheet
          entry={modal.entry}
          onClose={() => setModal(null)}
          onSave={async (ml) => { await api.patch(`/water/${modal.entry.id}`, { amount_ml: Number(ml) }); toast.success(lang === "en" ? "Updated" : "تم التعديل"); setModal(null); load(); }}
          onDelete={async () => { await api.delete(`/water/${modal.entry.id}`); toast.success(t("dash.deleted")); setModal(null); load(); }}
        />
      )}
      {modal?.kind === "exercise" && (
        <ExerciseModal date={selectedDate} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.kind === "log_meal" && logMealType && (
        <LogMealPicker
          mealType={logMealType}
          onClose={() => { setModal(null); setLogMealType(null); }}
          onPick={(kind) => {
            setModal(null);
            const t = logMealType; setLogMealType(null);
            // Preserve the currently-viewed day so past-day logging saves to
            // the right date (barcode / photo / describe / manual all honour
            // the ?date= URL param on AddMeal.jsx).
            const dq = selectedDate && selectedDate !== todayISO() ? `&date=${selectedDate}` : "";
            if (kind === "barcode")       nav(`/app/add?type=${t}&mode=barcode${dq}`);
            else if (kind === "label")    nav(`/app/add?type=${t}&mode=label${dq}`);
            else if (kind === "recipes")  nav(`/app/recipes?favs=1&type=${t}${dq}`);
            else if (kind === "camera")   nav(`/app/add?type=${t}&mode=photo${dq}`);
            else if (kind === "describe") nav(`/app/add?type=${t}&mode=describe${dq}`);
            else                          nav(`/app/add?type=${t}&mode=manual${dq}`);
          }}
        />
      )}
    </div>
  );
}

/* ---------- Calorie ring ---------- */
function CalorieRing({ consumed, target, size = 120, exceeded = false }) {
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const pct = target > 0 ? Math.min(1, consumed / target) : 0;
  const offset = c * (1 - pct);
  const ringColor = exceeded ? "#EF4444" : "#FF6B00";
  const badgeBg = exceeded ? "bg-red-500" : "bg-brand-500";
  const badgeShadow = exceeded ? "" : "shadow-brand";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#FFFFFF" strokeWidth={stroke} opacity={0.55} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={ringColor} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${badgeBg} ${badgeShadow}`}>
          <Zap className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

/* ---------- Macro card with ring (compact, vertical for mobile) ---------- */
function MacroCircle({ label, target, consumed, color, accent, emoji }) {
  const { t } = useI18n();
  const exceeded = target > 0 && consumed > target;
  const remaining = target - consumed; // may be negative
  const over = Math.max(0, consumed - target);
  const pct = target > 0 ? Math.min(1, consumed / target) : 0;
  const size = 56;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const offset = c * (1 - pct);
  const displayColor = exceeded ? "#EF4444" : color;
  const displayAccent = exceeded ? "#FEE2E2" : accent;
  const g = exceeded ? Math.round(over) : Math.round(Math.max(0, remaining));

  return (
    <div className="bg-white rounded-3xl shadow-card p-3 flex flex-col items-center gap-1.5 min-w-0" data-testid={`macro-${label}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#F7F5F2" strokeWidth={stroke} />
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={displayColor} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: displayAccent }}>
            <span className="text-sm">{emoji}</span>
          </div>
        </div>
      </div>
      <div className="font-black num text-sm text-center whitespace-nowrap" style={{ color: displayColor }} data-testid={`macro-${label}-value`} dir="ltr">
        {Math.round(consumed)}<span className="opacity-60 font-bold">/{Math.round(target)}g</span>
      </div>
      <div className={`text-[10px] leading-tight text-center ${exceeded ? "text-red-600 font-bold" : "text-neutral-500"}`}>
        {exceeded ? `${t("macro.over")} ${label} +${Math.round(over)}g` : label}
      </div>
    </div>
  );
}

/* ---------- Steps ring ---------- */
function StepsRing({ pct }) {
  const size = 64, stroke = 5;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const p = Math.min(100, Math.max(0, pct));
  const offset = c * (1 - p / 100);
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#F7F5F2" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#FF6B00" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
    </svg>
  );
}

/* ---------- Bottom sheet: log meal picker (5 methods) ---------- */
function LogMealPicker({ mealType, onClose, onPick }) {
  const { t, lang } = useI18n();
  const en = lang === "en";
  // Pending "tip → then flow" state. When the user taps an option we
  // check whether they've seen the tutorial for it before; if not, we
  // render the tip first, and only proceed to the real flow (camera,
  // scanner, describe form, ...) after they hit "فهمت". Seen tips go
  // straight through as before.
  const [pendingTip, setPendingTip] = useState(null); // string | null

  const tips = {
    camera: {
      icon: Camera,
      heroGradient: "from-orange-400 via-orange-500 to-rose-500",
      title:   en ? "Snap a clear photo" : "التقط صورة واضحة للوجبة",
      subtitle: en
        ? "The AI reads the plate best when every ingredient is visible."
        : "الذكاء الاصطناعي يحلّل الصورة أدق لمّا كل مكوّن في الوجبة يكون واضح.",
      tips: en ? [
        "Take the photo from directly above the plate.",
        "Make sure each ingredient is separate and unhidden.",
        "Avoid blurry shots or heavy filters — natural light is best.",
      ] : [
        "التقط الصورة من الأعلى مباشرة فوق الطبق.",
        "خلّي كل صنف من مكوّنات الوجبة واضح ومو مغطّى.",
        "تجنّب الصور الضبابية أو الفلاتر — الإضاءة الطبيعية أفضل.",
      ],
    },
    describe: {
      icon: Sparkles,
      heroGradient: "from-indigo-400 via-violet-500 to-fuchsia-500",
      title:    en ? "Describe your meal in words" : "اكتب وصف وجبتك",
      subtitle: en
        ? "Type or record what you ate — the AI turns it into full nutrition."
        : "اكتب أو سجّل صوت وش أكلت — والذكاء الاصطناعي يحسبها لك مباشرة.",
      tips: en ? [
        "Mention each ingredient with a rough amount (e.g. \"2 eggs, half a cup of rice\").",
        "Include cooking method when it matters (fried, grilled, boiled).",
        "You can talk in Saudi dialect — the AI understands it.",
      ] : [
        "اذكر كل صنف مع كميّته تقريباً (مثلاً \"بيضتين ونص كوب رز\").",
        "أضف طريقة الطبخ لمّا تفرق (مقلي، مشوي، مسلوق).",
        "تقدر تتكلم بلهجتك السعودية — الذكاء الاصطناعي يفهمها.",
      ],
    },
    barcode: {
      icon: BarcodeIcon,
      heroGradient: "from-emerald-400 via-teal-500 to-cyan-500",
      title:    en ? "Scan the product barcode" : "امسح باركود المنتج",
      subtitle: en
        ? "Point the camera at any packaged food's barcode."
        : "وجّه الكاميرا على الباركود اللي على العبوة.",
      tips: en ? [
        "Keep the barcode centered and in focus.",
        "Use good lighting — glare on the packaging can throw it off.",
        "If the product isn't in the database, snap its nutrition label and we'll read it.",
      ] : [
        "خلّي الباركود في منتصف الكاميرا وواضح.",
        "استخدم إضاءة جيدة — انعكاس الضوء على العبوة يشوّش الماسح.",
        "لو المنتج مو موجود، ننقلك لتصوير ملصق التغذية ونقراه لك تلقائياً.",
      ],
    },
    label: {
      icon: ScanLine,
      heroGradient: "from-fuchsia-400 via-purple-500 to-indigo-500",
      title:    en ? "Snap the nutrition label" : "صوّر ملصق التغذية",
      subtitle: en
        ? "Skip the barcode — AI reads the calories & macros straight from the label."
        : "بدون باركود — الذكاء الاصطناعي يقرأ السعرات والماكرو من الملصق مباشرة.",
      tips: en ? [
        "Get close so the numbers on the label are sharp and readable.",
        "Cover the whole panel — calories, protein, carbs, and fat.",
        "You'll be able to edit the amount you ate on the next screen.",
      ] : [
        "قرّب الكاميرا حتى تصير الأرقام على الملصق واضحة.",
        "تأكد إن كل الجدول ظاهر — السعرات والبروتين والكارب والدهون.",
        "بعد التصوير راح تقدر تعدّل الكمية اللي أكلتها في الشاشة اللي بعدها.",
      ],
    },
    manual: {
      icon: Edit3,
      heroGradient: "from-sky-400 via-blue-500 to-indigo-500",
      title:    en ? "Add ingredients yourself" : "أدخل المكوّنات يدوياً",
      subtitle: en
        ? "Full control — perfect for home cooking or custom recipes."
        : "تحكّم كامل — مثالي للأكل البيتي أو الوصفات الخاصة.",
      tips: en ? [
        "Type each ingredient's name and grams — we look up the macros automatically.",
        "Add as many rows as you need for a mixed meal.",
        "Save the meal as a recipe if you plan to eat it again.",
      ] : [
        "أكتب اسم كل مكوّن مع الجرامات — ونحسب الماكرو لك تلقائياً.",
        "تقدر تضيف أي عدد من الأصناف لوجبة متعدّدة.",
        "احفظها كوصفة لو تنوي تعيدها لاحقاً.",
      ],
    },
    recipes: {
      icon: BookOpen,
      heroGradient: "from-amber-400 via-orange-500 to-red-500",
      title:    en ? "Pick from your saved recipes" : "اختر من وصفاتك المحفوظة",
      subtitle: en
        ? "One tap to log a recipe you've already made."
        : "بضغطة واحدة تسجّل وصفة سبق سوّيتها.",
      tips: en ? [
        "Every meal you save appears here for future taps.",
        "Adjust the serving size before logging if you ate more or less.",
        "Recipes carry the exact macros you calculated the first time.",
      ] : [
        "كل وجبة تحفظها تلقائياً تظهر هنا لتسجّلها بضغطة.",
        "تقدر تعدّل حجم الحصة قبل التسجيل لو أكلت أكثر أو أقل.",
        "الوصفة تحمل نفس السعرات والماكرو اللي حسبتها أول مرة.",
      ],
    },
  };

  const handlePick = (kind) => {
    if (shouldShowTip(kind)) { setPendingTip(kind); return; }
    onPick(kind);
  };

  const activeTip = pendingTip ? tips[pendingTip] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose} dir="rtl">
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-[#F9F4EC] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 pb-8 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="log-meal-picker">
        <div className="w-12 h-1 bg-neutral-300 rounded-full mx-auto mb-1" />
        <MealOption icon={Camera}            label={t("dash.addmeal.camera")}      desc={t("dash.addmeal.camera_desc")}       onClick={() => handlePick("camera")}   testid="meal-opt-camera" />
        <MealOption icon={Sparkles}          label={t("dash.addmeal.describe")}    desc={t("dash.addmeal.describe_desc")}                   onClick={() => handlePick("describe")} testid="meal-opt-describe" badge={en ? "Most used · Most accurate" : "الأكثر استخداماً ودقة"} />
        <MealOption icon={BarcodeIcon}       label={t("dash.addmeal.barcode")}     desc={t("dash.addmeal.barcode_desc")}  onClick={() => handlePick("barcode")}  testid="meal-opt-barcode" />
        <MealOption icon={ScanLine}          label={t("dash.addmeal.label")}       desc={t("dash.addmeal.label_desc")}    onClick={() => handlePick("label")}    testid="meal-opt-label" />
        <MealOption icon={Edit3}             label={t("dash.addmeal.manual")}      desc={t("dash.addmeal.manual_desc")}                 onClick={() => handlePick("manual")}   testid="meal-opt-manual" />
        <MealOption icon={BookOpen}          label={t("dash.addmeal.recipes")}     desc={t("dash.addmeal.recipes_desc")}           onClick={() => handlePick("recipes")}  testid="meal-opt-recipes" />
      </div>

      {activeTip && (
        <FeatureTipModal
          featureKey={pendingTip}
          icon={activeTip.icon}
          heroGradient={activeTip.heroGradient}
          title={activeTip.title}
          subtitle={activeTip.subtitle}
          tips={activeTip.tips}
          onConfirm={() => { const k = pendingTip; setPendingTip(null); onPick(k); }}
          onClose={() => setPendingTip(null)}
        />
      )}
    </div>
  );
}

function MealOption({ icon: Icon, label, desc, onClick, testid, badge }) {
  return (
    <button
      onClick={onClick}
      dir="rtl"
      className="w-full bg-white rounded-2xl px-5 py-4 shadow-card flex flex-row-reverse items-center gap-4 hover:shadow-brand transition-shadow"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-2xl bg-brand-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-brand-500" />
      </div>
      <div className="flex-1 text-right min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-neutral-800">{label}</span>
          {badge && <span className="text-[10px] font-bold bg-brand-500 text-white rounded-full px-2 py-0.5 leading-4" data-testid={`${testid}-badge`}>{badge}</span>}
        </div>
        {desc && <div className="text-[11px] text-neutral-500 mt-0.5 line-clamp-1">{desc}</div>}
      </div>
    </button>
  );
}

/* ---------- Reusable quick input modal ---------- */
function QuickInputModal({ title, onSubmit, onClose, suffix = "", step = "1", placeholder = "", testid, initial = "" }) {
  const { lang } = useI18n();
  const [v, setV] = useState(initial);
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose} dir="rtl">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5" data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
          <div className="font-bold flex-1 text-end mr-3">{title}</div>
        </div>
        <div className="flex items-center gap-3 bg-neutral-50 border-2 border-brand-500 rounded-2xl px-4 py-3 mb-4" dir="ltr">
          <input type="number" step={step} value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} autoFocus
            className="flex-1 min-w-0 bg-transparent text-center text-2xl font-bold num focus:outline-none" data-testid={`${testid}-input`} />
          <span className="text-sm text-neutral-500 flex-shrink-0">{suffix}</span>
        </div>
        <button disabled={saving || !v}
          onClick={async () => { setSaving(true); try { await onSubmit(v); } finally { setSaving(false); } }}
          className="w-full orange-gradient text-white font-semibold py-3 rounded-full disabled:opacity-60"
          data-testid={`${testid}-save`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (lang === "en" ? "Save" : "حفظ")}
        </button>
      </div>
    </div>
  );
}

/* ---------- Exercise modal ---------- */
const EXERCISES = [
  { name: "المشي",       name_en: "Walking",     kcal_per_min: 4,  Icon: Footprints,  color: "#10B981" },
  { name: "الجري",       name_en: "Running",     kcal_per_min: 10, Icon: Zap,         color: "#EF4444" },
  { name: "الدراجة",     name_en: "Cycling",     kcal_per_min: 8,  Icon: Bike,        color: "#3B82F6" },
  { name: "السباحة",     name_en: "Swimming",    kcal_per_min: 9,  Icon: Waves,       color: "#06B6D4" },
  { name: "الأثقال",     name_en: "Weights",     kcal_per_min: 6,  Icon: Dumbbell,    color: "#8B5CF6" },
  { name: "اليوقا",      name_en: "Yoga",        kcal_per_min: 3,  Icon: HeartPulse,  color: "#EC4899" },
  { name: "كارديو HIIT", name_en: "HIIT cardio", kcal_per_min: 12, Icon: Flame,       color: "#F97316" },
  { name: "أخرى",        name_en: "Other",       kcal_per_min: 5,  Icon: Activity,    color: "#6B7280" },
];

// Resolve any stored exercise name to a visual (icon + colour). Falls back to
// the "أخرى" tile so custom AI-suggested activities still get a nice look.
// Supports partial matches (e.g., "ركوب الدراجة" → Bike) and English keywords.
function exerciseVisual(name = "") {
  const lower = String(name).toLowerCase().trim();
  const exact = EXERCISES.find((e) => e.name === name);
  if (exact) return exact;
  const rules = [
    { pat: /دراج|bike|cycl/i,        v: EXERCISES[2] },
    { pat: /سبح|swim/i,              v: EXERCISES[3] },
    { pat: /جري|running|run/i,       v: EXERCISES[1] },
    { pat: /مشي|walk|step/i,         v: EXERCISES[0] },
    { pat: /أثقال|ثقل|weight|gym|lift/i, v: EXERCISES[4] },
    { pat: /يوقا|yoga|تأمل/i,       v: EXERCISES[5] },
    { pat: /hiit|كارديو|cardio|جبل|hill|climb/i, v: EXERCISES[6] },
  ];
  const hit = rules.find((r) => r.pat.test(name) || r.pat.test(lower));
  return hit ? hit.v : EXERCISES[7];
}

function ExerciseModal({ date, onClose, onSaved }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [tab, setTab] = useState("manual"); // manual | ai
  const [pick, setPick] = useState(EXERCISES[0]);
  const [minutes, setMinutes] = useState(30);
  const [directKcal, setDirectKcal] = useState("");  // "أخرى" only: user-entered total calories
  const [saving, setSaving] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    { role: "ai", text: en ? "Tell me what exercise you did. Example: (I climbed a mountain for an hour)." : "أخبرني عن التمرين الذي قمت به. مثال: (تسلقت الجبال لمدة ساعة)." },
  ]);
  const [aiInput, setAiInput] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null); // {name, duration_min, calories_burned}

  const isOther = pick.name === "أخرى";
  const kcal = isOther
    ? (Number(directKcal) || 0)
    : Math.round((pick.kcal_per_min || 0) * (Number(minutes) || 0));

  const saveManual = async () => {
    setSaving(true);
    try {
      await api.post("/exercise", { date, name: pick.name, duration_min: Number(minutes), calories_burned: kcal });
      const displayName = en ? (pick.name_en || pick.name) : pick.name;
      toast.success(en ? `Logged ${displayName} (${kcal} kcal)` : `تم تسجيل ${displayName} (${kcal} سعرة)`);
      onSaved();
    } catch { toast.error(en ? "Couldn't log" : "تعذر التسجيل"); }
    finally { setSaving(false); }
  };

  const sendAi = async () => {
    const txt = aiInput.trim();
    if (!txt) return;
    setAiInput(""); setAiBusy(true);
    setAiMessages((m) => [...m, { role: "me", text: txt }]);
    try {
      const { data } = await api.post("/exercise/analyze/describe", { description: txt, language: lang });
      setAiSuggestion(data);
      setAiMessages((m) => [...m, { role: "ai", text: en
        ? `Looks like you did **${data.name}** for ${data.duration_min} minutes → about ${Math.round(data.calories_burned)} kcal. Save it?`
        : `يبدو أنك قمت بـ **${data.name}** لمدة ${data.duration_min} دقيقة → حوالي ${Math.round(data.calories_burned)} سعرة. أحفظه؟` }]);
    } catch {
      setAiMessages((m) => [...m, { role: "ai", text: en
        ? "Sorry, I couldn't understand the description. Please try again."
        : "عذراً، لم أستطع فهم الوصف. حاول مرة أخرى." }]);
    } finally { setAiBusy(false); }
  };

  const saveAi = async () => {
    if (!aiSuggestion) return;
    setSaving(true);
    try {
      await api.post("/exercise", { date, ...aiSuggestion });
      toast.success(en ? `Logged ${aiSuggestion.name}` : `تم تسجيل ${aiSuggestion.name}`);
      onSaved();
    } catch { toast.error(en ? "Couldn't log" : "تعذر التسجيل"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose} dir={en ? "ltr" : "rtl"}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-3xl p-5 max-h-[90vh] overflow-y-auto" data-testid="exercise-modal">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          <div className={`font-bold flex-1 mr-3 ${en ? "text-left" : "text-end"}`}>{en ? "Log exercise" : "تسجيل تمرين"}</div>
        </div>
        {/* Tabs */}
        <div className="flex bg-neutral-100 rounded-full p-1 mb-4">
          <button onClick={() => setTab("manual")}
            className={`flex-1 py-2 text-sm font-bold rounded-full transition-colors ${tab === "manual" ? "bg-white shadow-sm" : "text-neutral-500"}`}
            data-testid="exercise-tab-manual">{en ? "Manual" : "يدوي"}</button>
          <button onClick={() => setTab("ai")}
            className={`flex-1 py-2 text-sm font-bold rounded-full transition-colors ${tab === "ai" ? "bg-white shadow-sm" : "text-neutral-500"}`}
            data-testid="exercise-tab-ai">{en ? "💬 Ask AI" : "💬 اسأل الذكاء"}</button>
        </div>

        {tab === "manual" && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {EXERCISES.map((ex) => {
                const active = pick.name === ex.name;
                const { Icon, color } = ex;
                return (
                  <button key={ex.name} onClick={() => setPick(ex)}
                    className={`rounded-2xl p-3 text-sm font-semibold flex items-center gap-2 border-[1.5px] transition-colors ${
                      active ? "border-black text-white" : "border-transparent bg-neutral-50 text-neutral-700 hover:bg-neutral-100"
                    }`}
                    style={active ? { backgroundColor: color } : undefined}
                    dir={en ? "ltr" : "rtl"}
                    data-testid={`exercise-${ex.name}`}>
                    <span
                      className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        backgroundColor: active ? "rgba(255,255,255,0.22)" : `${color}18`,
                        border: active ? "1.5px solid rgba(255,255,255,0.4)" : `1.5px solid ${color}55`,
                      }}
                    >
                      <Icon className="w-4 h-4" style={{ color: active ? "#fff" : color }} strokeWidth={2.2} />
                    </span>
                    <span className={`flex-1 ${en ? "text-start" : "text-end"}`}>{en ? (ex.name_en || ex.name) : ex.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="mb-3">
              <div className={`text-xs text-neutral-500 mb-1.5 ${en ? "text-start" : "text-end"}`}>{en ? "Duration (minutes)" : "المدة (دقيقة)"}</div>
              <input type="number" min="1" value={minutes} onChange={(e) => setMinutes(e.target.value)}
                className={`w-full bg-neutral-50 border border-neutral-100 rounded-2xl px-4 py-3 text-lg font-bold num focus:outline-none ${en ? "text-start" : "text-end"}`} data-testid="exercise-minutes" />
            </div>
            {isOther && (
              <div className="mb-3">
                <div className={`text-xs text-neutral-500 mb-1.5 ${en ? "text-start" : "text-end"}`}>{en ? "Calories burned" : "السعرات المحروقة"}</div>
                <input type="number" step="1" min="0" value={directKcal} onChange={(e) => setDirectKcal(e.target.value)}
                  placeholder={en ? "Enter calories directly" : "اكتب السعرات مباشرة"}
                  className={`w-full bg-neutral-50 border border-neutral-100 rounded-2xl px-4 py-3 text-lg font-bold num focus:outline-none ${en ? "text-start" : "text-end"}`} data-testid="exercise-direct-kcal" />
              </div>
            )}
            {!isOther && (
              <div className="bg-brand-50 rounded-2xl p-3 mb-4 text-center">
                <div className="text-xs text-brand-700">{en ? "Calories burned (estimated)" : "السعرات المحروقة (تقديرياً)"}</div>
                <div className="text-2xl font-bold num text-brand-500">{kcal}</div>
              </div>
            )}
            <button onClick={saveManual} disabled={saving || !minutes || (isOther && !directKcal)}
              className="w-full orange-gradient text-white font-semibold py-3 rounded-full disabled:opacity-60" data-testid="exercise-save">
              {saving ? (en ? "Saving..." : "جاري الحفظ...") : (en ? "Save exercise" : "حفظ التمرين")}
            </button>
          </>
        )}

        {tab === "ai" && (
          <>
            <div className="bg-neutral-50 rounded-2xl p-3 space-y-2 max-h-60 overflow-y-auto mb-3" data-testid="exercise-ai-chat">
              {aiMessages.map((m, i) => (
                <div key={i} className={`text-sm ${m.role === "me" ? (en ? "text-start" : "text-end") : (en ? "text-end" : "text-start")}`}>
                  <span className={`inline-block px-3 py-2 rounded-2xl max-w-[85%] ${m.role === "me" ? "bg-brand-500 text-white" : "bg-white shadow-sm text-neutral-800"}`}>
                    {m.text}
                  </span>
                </div>
              ))}
              {aiBusy && <div className="text-xs text-neutral-500">{en ? "Thinking..." : "جارٍ التفكير..."}</div>}
            </div>
            <div className="flex gap-2 mb-3">
              <input value={aiInput} onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendAi()}
                placeholder={en ? "e.g. climbed a mountain for a full hour" : "مثال: تسلقت الجبال ساعة كاملة"}
                className={`flex-1 bg-neutral-50 rounded-2xl px-4 py-3 text-sm focus:outline-none ${en ? "text-start" : "text-end"}`} data-testid="exercise-ai-input" />
              <button onClick={sendAi} disabled={aiBusy || !aiInput.trim()}
                className="orange-gradient text-white w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-60" data-testid="exercise-ai-send">
                <Plus className="w-4 h-4 rotate-45" />
              </button>
            </div>
            {aiSuggestion && (
              <button onClick={saveAi} disabled={saving}
                className="w-full orange-gradient text-white font-semibold py-3 rounded-full disabled:opacity-60" data-testid="exercise-ai-save">
                {saving
                  ? (en ? "Loading..." : "جاري...")
                  : (en
                      ? `Save: ${aiSuggestion.name} (${Math.round(aiSuggestion.calories_burned)} kcal)`
                      : `احفظ: ${aiSuggestion.name} (${Math.round(aiSuggestion.calories_burned)} سعرة)`)}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Water picker (choose amount) ---------- */
const WATER_PRESETS = [100, 200, 250, 330, 500, 750];

function WaterPicker({ onClose, onSubmit }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [custom, setCustom] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose} dir={en ? "ltr" : "rtl"}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5" data-testid="water-picker">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          <div className="font-bold flex-1 text-end mr-3">{en ? "Add water" : "أضف الماء"}</div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {WATER_PRESETS.map((ml) => (
            <button key={ml} onClick={() => onSubmit(ml)}
              className="bg-sky-50 hover:bg-sky-100 border border-sky-100 rounded-2xl py-3 font-bold num text-sky-700"
              data-testid={`water-preset-${ml}`}>{ml}ml</button>
          ))}
        </div>
        <div className="text-xs text-neutral-500 mb-1.5 text-end">{en ? "Custom amount" : "كمية مخصصة"}</div>
        <div className="flex gap-2">
          <input type="number" min="10" max="2000" value={custom} onChange={(e) => setCustom(e.target.value)}
            placeholder={en ? "e.g. 350" : "مثال: 350"} data-testid="water-custom-input"
            className={`flex-1 bg-neutral-50 border border-neutral-100 rounded-2xl px-4 py-3 num focus:outline-none ${en ? "text-start" : "text-end"}`} />
          <button onClick={() => custom && onSubmit(Number(custom))} disabled={!custom || Number(custom) <= 0}
            className="orange-gradient text-white font-bold px-5 rounded-2xl disabled:opacity-60" data-testid="water-custom-save">{en ? "Add" : "أضف"}</button>
        </div>
      </div>
    </div>
  );
}

/* Edit the DAILY water TARGET (goal), not a single glass. Offers common
   presets in litres for one-tap picking + a custom input in litres. */
function WaterGoalModal({ initial, onClose, onSubmit }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const initialL = ((Number(initial) || 2000) / 1000).toFixed(2).replace(/\.?0+$/, "");
  const [litres, setLitres] = useState(initialL);
  const PRESETS_L = [1.5, 2, 2.5, 3, 3.5, 4];
  const parsed = Number(litres);
  const valid = parsed >= 0.5 && parsed <= 8;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose} dir={en ? "ltr" : "rtl"}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5" data-testid="water-goal-modal">
        <div className="flex items-center justify-between mb-1">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center" data-testid="water-goal-close"><X className="w-4 h-4" /></button>
          <div className={`font-bold flex-1 mr-3 ${en ? "text-left" : "text-right"}`}>{en ? "Daily water goal" : "هدف الماء اليومي"}</div>
        </div>
        <div className={`text-[11px] text-neutral-500 mb-3 ${en ? "text-left" : "text-right"}`}>{en ? "Pick a common amount or enter a value in litres" : "اختر كمية شائعة أو أدخل رقماً بالتر"}</div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {PRESETS_L.map((l) => (
            <button key={l} onClick={() => setLitres(String(l))}
              className={`rounded-2xl py-2.5 font-bold num transition-all ${
                Number(litres) === l
                  ? "bg-sky-500 text-white shadow-sm"
                  : "bg-sky-50 hover:bg-sky-100 border border-sky-100 text-sky-700"
              }`}
              data-testid={`water-goal-preset-${l}`}>
              <span dir="ltr">{l}</span> <span className="text-[10px] font-semibold">{en ? "L" : "لتر"}</span>
            </button>
          ))}
        </div>
        <div className={`text-[11px] text-neutral-500 mb-1.5 ${en ? "text-left" : "text-right"}`}>{en ? "Custom value" : "قيمة مخصصة"}</div>
        <div className="flex gap-2 items-stretch">
          <input
            type="number" min="0.5" max="8" step="0.1" value={litres}
            onChange={(e) => setLitres(e.target.value)}
            placeholder="2.5"
            className={`flex-1 bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-3 num focus:outline-none focus:border-sky-300 ${en ? "text-left" : "text-right"}`}
            data-testid="water-goal-input"
          />
          <div className="flex items-center px-3 rounded-2xl bg-neutral-100 text-neutral-600 font-semibold text-sm">{en ? "L" : "لتر"}</div>
        </div>
        {!valid && (
          <div className={`text-[11px] text-red-500 mt-1.5 ${en ? "text-left" : "text-right"}`}>{en ? "Value must be between 0.5 and 8 litres" : "القيمة يجب أن تكون بين 0.5 و 8 لتر"}</div>
        )}
        <button
          onClick={() => valid && onSubmit(Math.round(parsed * 1000))}
          disabled={!valid}
          className="w-full mt-4 orange-gradient text-white font-bold py-3 rounded-2xl disabled:opacity-50"
          data-testid="water-goal-save"
        >
          {en ? "Save" : "حفظ"}
        </button>
      </div>
    </div>
  );
}

function WaterEditSheet({ entry, onClose, onSave, onDelete }) {
  const [ml, setMl] = useState(String(entry.amount_ml));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose} dir="rtl">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5" data-testid="water-edit-sheet">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          <div className="font-bold flex-1 text-end mr-3">تعديل الماء</div>
        </div>
        <div className="flex items-center gap-3 bg-neutral-50 border-2 border-brand-500 rounded-2xl px-4 py-3 mb-4" dir="ltr">
          <input type="number" value={ml} onChange={(e) => setMl(e.target.value)} autoFocus
            className="flex-1 min-w-0 bg-transparent text-center text-2xl font-bold num focus:outline-none" data-testid="water-edit-input" />
          <span className="text-sm text-neutral-500 flex-shrink-0">ml</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSave(ml)} disabled={!ml || Number(ml) <= 0}
            className="flex-1 orange-gradient text-white font-semibold py-3 rounded-full disabled:opacity-60" data-testid="water-edit-save">حفظ</button>
          <button onClick={async () => {
            if (!(await confirm({ title: "حذف هذا الكوب؟", confirmLabel: "حذف", danger: true }))) return;
            onDelete();
          }}
            className="bg-red-50 text-red-500 w-14 rounded-full flex items-center justify-center" data-testid="water-edit-delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Meal detail (in-line, matches iOS mockup): 4 macro rings + table + notes + actions ---------- */
function MealDetail({ meal, onChanged, onDeleteMeal, onEditMeal }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [items, setItems] = useState(meal.items || []);
  const [notes, setNotes] = useState(meal.notes || "");
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Recompute totals from local items (kept in sync when user deletes rows)
  const totals = items.reduce((acc, it) => ({
    calories: acc.calories + Number(it.calories || 0),
    protein:  acc.protein  + Number(it.protein_g || 0),
    carbs:    acc.carbs    + Number(it.carbs_g   || 0),
    fat:      acc.fat      + Number(it.fat_g     || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const removeItem = async (idx) => {
    const it = items[idx];
    const ok = await confirm({
      title: en ? "Delete ingredient?" : "حذف المكون؟",
      description: `${it.name} — ${fmtInt(it.calories)} ${en ? "kcal" : "سعرة"}`,
      confirmLabel: en ? "Delete" : "حذف",
      danger: true,
    });
    if (!ok) return;
    const next = items.filter((_, i) => i !== idx);
    try {
      const { data } = await api.patch(`/meals/${meal.id}`, { items: next });
      if (data?.deleted) {
        toast.success(en ? "Meal deleted" : "تم حذف الوجبة");
        onChanged();
        return;
      }
      setItems(next);
      toast.success(en ? "Ingredient removed" : "تم حذف المكون");
      onChanged();
    } catch { toast.error(en ? "Couldn't delete" : "تعذر الحذف"); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/meals/${meal.id}`, { notes: splitPoints(notes).join("\n") });
      toast.success(lang === "en" ? "Notes saved" : "تم حفظ الملاحظات");
      onChanged();
    } catch { toast.error(lang === "en" ? "Couldn't save" : "تعذر الحفظ"); }
    finally { setSavingNotes(false); }
  };

  const saveAsRecipe = async () => {
    if (items.length === 0) return toast.error("لا توجد مكونات");
    setSavingRecipe(true);
    try {
      // Personal manual recipes now live alongside AI-imported ones in the
      // user_recipes collection so the trainee sees them all in one list.
      await api.post("/user-recipes/from-meal", {
        name: meal.name,
        servings: 1,
        notes: splitPoints(notes).join("\n") || null,
        items: items.map((it) => ({
          name: it.name,
          quantity_g: Number(it.quantity_g) || 100,
          unit: it.unit || "g",
          calories: Number(it.calories) || 0,
          protein_g: Number(it.protein_g) || 0,
          carbs_g: Number(it.carbs_g) || 0,
          fat_g: Number(it.fat_g) || 0,
        })),
      });
      toast.success("حُفظت في مفضلاتي ✅");
    } catch {
      toast.error("تعذر حفظ الوصفة");
    } finally { setSavingRecipe(false); }
  };

  const MACRO_CARDS = [
    { label: "السعرات الحرارية", value: totals.calories, color: "#F5B301", accent: "#FEF3C7", icon: "🔥", key: "cal" },
    { label: "الكربوهيدرات",   value: totals.carbs,    color: "#22C55E", accent: "#D1FAE5", icon: "🌾", key: "carbs", suffix: "غ" },
    { label: "الدهون",          value: totals.fat,      color: "#A16207", accent: "#FEF3C7", icon: "🥑", key: "fat",   suffix: "غ" },
    { label: "البروتين",        value: totals.protein,  color: "#38BDF8", accent: "#DCF3FE", icon: "🥩", key: "prot",  suffix: "غ" },
  ];

  return (
    <div className="px-4 pb-4 pt-1 bg-neutral-50/60" data-testid={`meal-detail-${meal.id}`}>
      <MacroStrip items={items} en={en} testid={`meal-totals-${meal.id}`} />

      {/* Items list — one card per food with a neat 2-line layout so words don't overlap */}
      <div className="space-y-2 mb-3" dir={en ? "ltr" : "rtl"}>
        {items.length === 0 && (
          <div className="bg-white rounded-2xl shadow-card text-center text-xs text-neutral-400 py-6">{en ? "No ingredients" : "لا توجد مكونات"}</div>
        )}
        {items.map((it, idx) => (
          <ItemCard key={idx} it={it} en={en} onDelete={() => removeItem(idx)} testid={`meal-item-row-${meal.id}-${idx}`} />
        ))}
      </div>

      {/* Notes section */}
      <div className="bg-white rounded-2xl shadow-card p-3 mb-3">
        <div className={`text-[11px] text-neutral-500 mb-1.5 font-semibold ${en ? "text-left" : "text-end"}`}>{en ? "Notes" : "ملاحظات"}</div>
        <NotesPoints value={notes} onChange={setNotes} en={en} testid={`meal-notes-${meal.id}`} />
        {splitPoints(notes).join("\n") !== splitPoints(meal.notes).join("\n") && (
          <button onClick={saveNotes} disabled={savingNotes}
            className="mt-2 text-xs text-brand-600 font-semibold" data-testid={`meal-notes-save-${meal.id}`}>
            {savingNotes ? (en ? "Saving..." : "جارٍ الحفظ...") : (en ? "Save notes" : "حفظ الملاحظات")}
          </button>
        )}
      </div>

      {/* Actions — 3 equal-width pills: save-as-recipe, edit meal, delete.
          Edit reopens AddMeal in "edit mode" preloaded with this meal, so
          the user can adjust anything (name, ingredients, notes, meal
          type) using the same input surface they used to create it. */}
      <div className="flex gap-2">
        <button
          onClick={saveAsRecipe}
          disabled={savingRecipe || items.length === 0}
          className="flex-1 bg-brand-500 text-white font-bold text-xs py-2.5 rounded-full flex items-center justify-center gap-1.5 disabled:opacity-60"
          data-testid={`meal-save-recipe-${meal.id}`}
        >
          <BookOpen className="w-3.5 h-3.5" /> {savingRecipe ? (en ? "Saving..." : "جاري الحفظ...") : (en ? "Save" : "حفظ")}
        </button>
        <button
          onClick={onEditMeal}
          className="flex-1 bg-neutral-900 text-white font-bold text-xs py-2.5 rounded-full flex items-center justify-center gap-1.5"
          data-testid={`meal-edit-${meal.id}`}
        >
          <Pencil className="w-3.5 h-3.5" /> {en ? "Edit" : "تعديل"}
        </button>
        <button
          onClick={onDeleteMeal}
          className="flex-1 bg-red-50 text-red-500 font-bold text-xs py-2.5 rounded-full flex items-center justify-center gap-1.5"
          data-testid={`meal-delete-${meal.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> {en ? "Delete" : "حذف"}
        </button>
      </div>
    </div>
  );
}

/* ---------- Small ring used inside meal detail macro cards ---------- */
function MacroDetailRing({ value, target = 100, color = "#FF6B00", label = "0" }) {
  const size = 68, stroke = 6;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const pct = target > 0 ? Math.min(1, Math.max(0, value / target)) : 0;
  const offset = c * (1 - pct);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#F7F5F2" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[13px] font-black num" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}



/* ---------- Glass tumbler shape used in the water tracker ----------
   Renders a trapezoidal glass; the blue water level dynamically reflects
   `amountMl / fullMl` (clamped 0–1). Set `empty` for the "+" add slot. */
function WaterGlass({ amountMl = 0, fullMl = 250, empty = false }) {
  const frac = empty ? 0 : Math.max(0, Math.min(1, amountMl / (fullMl || 250)));
  // Inner water region occupies y=12 (top, just under rim) → y=48 (near bottom).
  const TOP = 12, BOTTOM = 48;
  const waterTop = BOTTOM - frac * (BOTTOM - TOP);
  // Interpolate the trapezoid left/right x at waterTop (glass is wider up top).
  // Glass sides go from x=6.5..33.5 at y=12 to x=9..31 at y=48 (approx).
  const t = (waterTop - TOP) / (BOTTOM - TOP); // 0 at top, 1 at bottom
  const leftX = 6.5 + (9 - 6.5) * t;
  const rightX = 33.5 - (33.5 - 31) * t;
  return (
    <div className="aspect-[3/4] w-full relative">
      <svg viewBox="0 0 40 52" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {/* Outline */}
        <path
          d="M4 3 Q4 2 5 2 L35 2 Q36 2 36 3 L32 46 Q31.5 50 27.5 50 L12.5 50 Q8.5 50 8 46 Z"
          fill={empty ? "#EFF6FF" : "#F0F9FF"}
          stroke="#0284C7"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        {frac > 0 && (
          <>
            {/* Water body — polygon from waterTop down to the rounded base */}
            <path
              d={`M${leftX} ${waterTop} L${rightX} ${waterTop} L30.4 45.2 Q30 48 27 48 L13 48 Q10 48 9.6 45.2 Z`}
              fill="#38BDF8"
            />
            {/* Wavy surface at the water line */}
            <path
              d={`M${leftX} ${waterTop} Q${leftX + 4} ${waterTop - 1.5} ${leftX + 7} ${waterTop} T${leftX + 14} ${waterTop} T${leftX + 21} ${waterTop} T${rightX} ${waterTop} L${rightX} ${waterTop + 1.6} L${leftX} ${waterTop + 1.6} Z`}
              fill="#7DD3FC"
            />
          </>
        )}
      </svg>
      {empty && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-sky-500 text-xl font-black leading-none">+</span>
        </div>
      )}
    </div>
  );
}
